import React, { FC, useCallback, useMemo, useState } from 'react';
import BulkTransferFromStockLocationModal from './BulkTransferFromStockLocationModal';
import TransferToLocationReceivedEquipmentBulk from './TransferToLocationReceivedEquipmentBulk';
import EPSLScheduledTransferToStockLocationModal from './EPSLScheduledTransferToStockLocationModal';
import EPSLScheduledTransferToLocationModal from './EPSLScheduledTransferToLocationModal';
import { Button, Dropdown, Menu, Popconfirm } from 'antd';
import { connect } from 'react-redux';
import {
	bulkTransferEquipmentsPerStockLocactionForSupplier,
	cancelScheduledEPSLTransfer,
} from '../../thunks/equipment_per_stock_locations_thunks';
import { ROLE_TYPES } from '../../utils/DataConstants';

const TransferEPSLButton: FC<any> = ({
	stockLocationId,
	onTransferSuccess,
	selectedRecords,
	buttonSize = 'large',
	buttonType = 'primary',
	ghost,

	bulkTransfer,
	cancelScheduledTransfer,
}): React.ReactElement => {
	const [transferLoading, setTransferLoading] = useState(false);

	const [transferModalVisible, setTransferModalVisible] = useState(null);
	const [transferBulkToLocationVisible, setTransferBulkToLocationVisible] = useState(null);
	const [showSLModal, setShowSLModal] = useState(false);
	const [showLocationModal, setShowLocationModal] = useState(false);

	const _onTransferClick = (type) => setTransferModalVisible(type);
	const _hideTransferModal = () => setTransferModalVisible(null);

	const onTransferEquipmentsSuccess = useCallback(() => {
		_hideTransferModal();
		setTransferBulkToLocationVisible(false);
		setShowSLModal(false);
		setShowLocationModal(false);
		onTransferSuccess();
	}, [onTransferSuccess]);

	const onTransfer = useCallback(
		(values) => {
			const updatedRecords = selectedRecords.map((record) => ({
				...record,
				id: `${record.equipmentUniqueId || record.id}`,
				stockLocationId: values.toStockLocationId,
				...(values.transferNote && { transferNote: values.transferNote }),
			}));

			bulkTransfer(updatedRecords).then(() => onTransferEquipmentsSuccess());
		},
		[bulkTransfer, selectedRecords, onTransferEquipmentsSuccess]
	);

	const hasScheduledSelected = useMemo(
		() => selectedRecords.some((r) => r.nextScheduledTransfer),
		[selectedRecords]
	);

	const epslIds = useMemo(() => selectedRecords.map((r) => r.id), [selectedRecords]);

	const isMultiple = useMemo(() => selectedRecords.length > 1, [selectedRecords.length]);

	const transferMenu = useMemo(
		() => (
			<Menu>
				<Menu.ItemGroup key="location" title="To Location">
					<Menu.Item onClick={() => setShowLocationModal(true)}>Single Location</Menu.Item>
					<Menu.Item
						disabled={!isMultiple}
						onClick={() => {
							setTransferBulkToLocationVisible(true);
							setTransferLoading(true);
						}}
					>
						Multiple Locations
					</Menu.Item>
				</Menu.ItemGroup>
				<Menu.ItemGroup key="stockLocation" title="To Stock Location">
					<Menu.Item onClick={() => setShowSLModal(true)}>Single Stock Location</Menu.Item>
					<Menu.Item
						disabled={!isMultiple}
						onClick={() => {
							_onTransferClick('stockLocation');
						}}
					>
						Multiple Stock Locations
					</Menu.Item>
				</Menu.ItemGroup>
			</Menu>
		),
		[isMultiple]
	);

	const handleCancelTransfer = useCallback(() => {
		cancelScheduledTransfer({
			equipmentPerStockLocationIds: epslIds,
		}).then(() => onTransferSuccess());
	}, [cancelScheduledTransfer, epslIds, onTransferSuccess]);

	return (
		<>
			{hasScheduledSelected ? (
				<Popconfirm
					title={`Cancel scheduled transfer for ${selectedRecords.length} item(s)?`}
					onConfirm={handleCancelTransfer}
					okText="Cancel Transfer"
					cancelText="Keep"
					okButtonProps={{ danger: true }}
				>
					<Button className="ml-2" size={buttonSize} danger type="primary">
						Cancel Transfer
					</Button>
				</Popconfirm>
			) : (
				<Dropdown overlay={transferMenu}>
					<Button
						className="ml-2"
						size={buttonSize}
						type={buttonType}
						ghost={ghost}
						loading={transferLoading}
					>
						Transfer
					</Button>
				</Dropdown>
			)}
			{transferModalVisible && (
				<BulkTransferFromStockLocationModal
					onCancel={_hideTransferModal}
					stockLocationId={stockLocationId}
					selectedEpsls={selectedRecords}
					onTransferToSL={onTransfer}
					onSuccess={onTransferEquipmentsSuccess}
					locationType={transferModalVisible}
				/>
			)}
			{transferBulkToLocationVisible && (
				<TransferToLocationReceivedEquipmentBulk
					selectedRecords={selectedRecords}
					onSuccess={onTransferEquipmentsSuccess}
					onClose={() => setTransferBulkToLocationVisible(false)}
					transferInitiated={() => setTransferLoading(false)}
				/>
			)}
			{showSLModal && (
				<EPSLScheduledTransferToStockLocationModal
					epslIds={epslIds}
					currentStockLocationId={stockLocationId}
					onCancel={() => setShowSLModal(false)}
					onSuccess={onTransferEquipmentsSuccess}
				/>
			)}
			{showLocationModal && (
				<EPSLScheduledTransferToLocationModal
					epslIds={epslIds}
					onCancel={() => setShowLocationModal(false)}
					onSuccess={onTransferEquipmentsSuccess}
				/>
			)}
		</>
	);
};

const mapDispatchToProps = (dispatch) => ({
	bulkTransfer: (entities) =>
		dispatch(bulkTransferEquipmentsPerStockLocactionForSupplier(entities)),
	cancelScheduledTransfer: (data) =>
		dispatch(cancelScheduledEPSLTransfer(ROLE_TYPES.SUPPLIER)(data)),
});

export default connect(null, mapDispatchToProps)(TransferEPSLButton);
